import { useEnterContest } from '@hooks/apiHooks/myCreationsHooks';
import { useStationContest, useStationPrintEnabled } from '@hooks/station';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { compositeImageWithQR } from '@utils/compositeImageWithQR';
import { getOrganizationId } from '@utils/getOrganization';
import {
  sendPrintMessage,
  sendSpeakMessage,
  sendStartOverMessage,
  sendStopTalkingMessage,
  setHandleNextFunction,
  setHandleShareFunction,
  setSwitchToNextVariantFunction,
} from '@utils/station';
import {
  buildCompanionSendMessage,
  MUSIC_GENERATION_COMPANION_MESSAGES,
} from '../../../features/AvatarSession/constants/musicGenerationAvatarMessages';
import QRCode from 'qrcode';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { DemoFan } from '../../../datas/demoFan';
import { useWaveformGenerator } from '../../../hooks/useWaveformGenerator';
import { GenerationResponse } from '../../../services/generationsApi';
import { OidcUser } from '../../../types/oidcUser';
import { isStreamUrl } from '../../../utils/streamUtils';
import { convertSecondsToTime } from '../../../utils/timeFormat';
import { StationContestModal } from './station/StationContestModal/StationContestModal';
import { StationCoverView } from './station/StationCoverView/StationCoverView';
import { StationFinalView } from './station/StationFinalView/StationFinalView';
import { StationGlowAnimation } from './station/StationGlowAnimation/StationGlowAnimation';
import { StationLyricsView } from './station/StationLyricsView/StationLyricsView';

interface StationMusicGenerationUIProps {
  musicGeneration?: GenerationResponse;
  fanData?: DemoFan;
  fanSlug: string;
  editedLyrics: string | null;
  onLyricsChange: (lyrics: string) => void;
  onProduce: () => void;
  song1Ready: boolean;
  song2Ready: boolean;
  song1SavedId?: string | null;
  song2SavedId?: string | null;
  isRegeneratingLyrics?: boolean;
  isRegeneratingMusic?: boolean;
  isRegeneratingImage?: boolean;
  onCardClick?: (songVersion: 1 | 2) => void;
  onRegenerateCover?: (songVersion: 1 | 2) => void;
  onSongVersionChange?: (version: 1 | 2) => void;
  musicCoverOverrides?: Partial<Record<1 | 2, string>>;
}

type StationState = 'lyrics' | 'cover' | 'final';

export const StationMusicGenerationUI = ({
  musicGeneration,
  editedLyrics,
  onLyricsChange,
  onProduce,
  song1Ready,
  song2Ready,
  song1SavedId,
  song2SavedId,
  isRegeneratingLyrics = false,
  isRegeneratingMusic = false,
  isRegeneratingImage = false,
  onCardClick,
  onSongVersionChange,
  musicCoverOverrides,
}: StationMusicGenerationUIProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const orgId = useMemo(() => getOrganizationId(user as OidcUser), [user]);
  const { volume, setVolume } = useMusicState();
  const queryClient = useQueryClient();
  const [currentState, setCurrentState] = useState<StationState>('lyrics');
  const prevStationStateRef = useRef<StationState>('lyrics');
  const [currentSongVersion, setCurrentSongVersion] = useState<1 | 2>(1);
  const [qrCodeDataUrl1, setQrCodeDataUrl1] = useState<string>('');
  const [qrCodeDataUrl2, setQrCodeDataUrl2] = useState<string>('');
  const [isPrinting, setIsPrinting] = useState(false);
  const [isUserEditing, setIsUserEditing] = useState(false);
  const lyricsContentRef = useRef<HTMLDivElement>(null);

  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
  const swipeContainerRef = useRef<HTMLDivElement>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(0);
  const currentTimeRef = useRef(0);
  const previousSongVersionRef = useRef<1 | 2>(currentSongVersion);
  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null);
  const [showExtendedMessage, setShowExtendedMessage] = useState(false);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const productionStartTimeRef = useRef<number | null>(null);
  const prevConditionsRef = useRef<{
    shouldStart: boolean;
    shouldStop: boolean;
  } | null>(null);
  const qrCodeSavedIdRef = useRef<string | null | undefined>(null);
  const [compositedCoverUrl, setCompositedCoverUrl] = useState<string | null>(
    null,
  );
  const playRetryAttemptRef = useRef<boolean>(false);

  const { isContestMode, contestId } = useStationContest();
  const isPrintEnabled = useStationPrintEnabled();
  const { mutateAsync: enterContest } = useEnterContest();
  const [isContestModalOpen, setIsContestModalOpen] = useState(false);
  const [contestEmail, setContestEmail] = useState<string | null>(null);
  const contestEnteredIdsRef = useRef<Set<string>>(new Set());

  const displayLyrics = editedLyrics ?? musicGeneration?.lyrics ?? '';
  const displayTitle =
    currentSongVersion === 1
      ? musicGeneration?.title1 || ''
      : musicGeneration?.title2 || '';

  const formatLyricsToHtml = useCallback((text: string) => {
    const lines = text.split('\n');
    return lines
      .map((line) => {
        const trimmed = line.trim();
        if (trimmed.match(/^\[.*\]$/)) {
          return `<div style="color: rgba(255, 255, 255, 0.40); font-family: 'Times New Roman', serif; font-size: 24px; font-style: italic; font-weight: 400; line-height: 36px; letter-spacing: -0.48px; text-align: left; margin-bottom: 24px;">${trimmed}</div>`;
        }
        if (trimmed) {
          return `<div style="color: rgba(255, 255, 255, 0.90); font-family: 'Times New Roman', serif; font-size: 32px; font-style: normal; font-weight: 400; line-height: 36px; letter-spacing: -0.64px; text-align: left;">${trimmed}</div>`;
        }
        return '<div><br></div>';
      })
      .join('');
  }, []);

  useEffect(() => {
    if (displayLyrics && !isUserEditing && lyricsContentRef.current) {
      const htmlContent = formatLyricsToHtml(displayLyrics);
      lyricsContentRef.current.innerHTML = htmlContent;
    }
  }, [displayLyrics, isUserEditing, formatLyricsToHtml]);

  useEffect(() => {
    const prev = prevStationStateRef.current;
    prevStationStateRef.current = currentState;
    if (prev !== 'cover' && currentState === 'cover') {
      sendSpeakMessage(
        buildCompanionSendMessage(
          MUSIC_GENERATION_COMPANION_MESSAGES.coverArtPhase,
          true,
          false,
        ).data,
      );
    }
  }, [currentState]);

  const displayCover =
    currentSongVersion === 1
      ? musicCoverOverrides?.[1] ?? musicGeneration?.thumbnailUrl1
      : musicCoverOverrides?.[2] ?? musicGeneration?.thumbnailUrl2;

  const songUrl1Ref = useRef<string | undefined>(musicGeneration?.songUrl1);
  const songUrl2Ref = useRef<string | undefined>(musicGeneration?.songUrl2);

  useEffect(() => {
    if (musicGeneration?.songUrl1) {
      songUrl1Ref.current = musicGeneration.songUrl1;
    }
    if (musicGeneration?.songUrl2) {
      songUrl2Ref.current = musicGeneration.songUrl2;
    }
  }, [musicGeneration?.songUrl1, musicGeneration?.songUrl2]);

  const cachedGeneration = musicGeneration?.taskId
    ? (queryClient.getQueryData<GenerationResponse>([
        'generationStatus',
        musicGeneration.taskId,
      ]) as GenerationResponse | undefined)
    : undefined;

  const currentSongUrl =
    currentSongVersion === 1
      ? musicGeneration?.songUrl1 ??
        cachedGeneration?.songUrl1 ??
        songUrl1Ref.current
      : musicGeneration?.songUrl2 ??
        cachedGeneration?.songUrl2 ??
        songUrl2Ref.current;

  const currentSongReady = currentSongVersion === 1 ? song1Ready : song2Ready;
  const songsAreReady = song1Ready && song2Ready;

  const currentSavedId = currentSongVersion === 1 ? song1SavedId : song2SavedId;
  const currentQrCodeDataUrl =
    currentSongVersion === 1 ? qrCodeDataUrl1 : qrCodeDataUrl2;

  const { waveformData } = useWaveformGenerator({
    songUrl: currentSongUrl,
    isReady: currentSongReady,
    isStream: isStreamUrl(currentSongUrl || ''),
    bars: 60,
  });

  useEffect(() => {
    if (
      currentSavedId &&
      currentQrCodeDataUrl &&
      displayCover &&
      currentState === 'cover' &&
      !isRegeneratingImage
    ) {
      compositeImageWithQR(displayCover, currentQrCodeDataUrl)
        .then((composited) => {
          if (
            currentSavedId &&
            currentQrCodeDataUrl &&
            displayCover &&
            currentState === 'cover'
          ) {
            setCompositedCoverUrl(composited);
          }
        })
        .catch((error) => {
          console.error('Error compositing cover with QR code:', error);
          setCompositedCoverUrl(null);
        });
    } else {
      setCompositedCoverUrl(null);
    }
  }, [
    currentSavedId,
    currentQrCodeDataUrl,
    displayCover,
    currentState,
    currentSongVersion,
    isRegeneratingImage,
  ]);

  const displayCoverWithQR = compositedCoverUrl || displayCover;

  const hasLyrics = !!musicGeneration?.lyrics;
  const hasCover = !!displayCover;

  const hasMultipleVariations = !!(
    musicGeneration?.thumbnailUrl1 && musicGeneration?.thumbnailUrl2
  );
  const isGeneratingOrRegenerating =
    (currentState === 'lyrics' && !hasLyrics) ||
    isRegeneratingLyrics ||
    isRegeneratingMusic ||
    isRegeneratingImage ||
    (currentState === 'cover' && !hasCover);

  const USE_IMAGE_BACKGROUND = true;
  const showCoverGlassEffect = currentState === 'cover' && isRegeneratingImage;
  const isGeneratingImage =
    currentState === 'cover' && !hasCover && !isRegeneratingImage;

  useEffect(() => {
    if (currentState === 'cover' && onSongVersionChange) {
      onSongVersionChange(currentSongVersion);
    }
  }, [currentState, currentSongVersion, onSongVersionChange]);

  useEffect(() => {
    const shouldStartCountdown = isRegeneratingMusic && !songsAreReady;
    const shouldStopCountdown = songsAreReady;

    const prev = prevConditionsRef.current;
    const conditionsChanged =
      !prev ||
      prev.shouldStart !== shouldStartCountdown ||
      prev.shouldStop !== shouldStopCountdown;

    if (!conditionsChanged) return;

    prevConditionsRef.current = {
      shouldStart: shouldStartCountdown,
      shouldStop: shouldStopCountdown,
    };

    if (shouldStartCountdown && countdownSeconds === null) {
      setCountdownSeconds(129);
      setShowExtendedMessage(false);
      productionStartTimeRef.current = Date.now();
    } else if (shouldStopCountdown && countdownSeconds !== null) {
      setCountdownSeconds(null);
      setShowExtendedMessage(false);
      productionStartTimeRef.current = null;
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }
  }, [isRegeneratingMusic, songsAreReady]);

  useEffect(() => {
    if (countdownSeconds === null || countdownSeconds <= 0) {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      if (
        countdownSeconds === 0 &&
        isRegeneratingMusic &&
        !songsAreReady &&
        !showExtendedMessage
      ) {
        setShowExtendedMessage(true);
      }
      return;
    }

    countdownIntervalRef.current = setInterval(() => {
      setCountdownSeconds((prev) => {
        if (prev === null || prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [
    countdownSeconds,
    isRegeneratingMusic,
    songsAreReady,
    showExtendedMessage,
  ]);

  useEffect(() => {
    const trackId = song1SavedId;
    if (song1Ready && orgId && !qrCodeDataUrl1 && trackId) {
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/creations/track/${orgId}/${trackId}`;

      QRCode.toDataURL(url, {
        width: 300,
        margin: 2,
        errorCorrectionLevel: 'H',
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      })
        .then((dataUrl) => {
          setQrCodeDataUrl1(dataUrl);
        })
        .catch((err) => {
          console.error(
            '[QR Code] Failed to generate QR code for song 1:',
            err,
          );
        });
    }
  }, [song1Ready, song1SavedId, qrCodeDataUrl1, orgId]);

  useEffect(() => {
    const trackId = song2SavedId;
    if (song2Ready && orgId && !qrCodeDataUrl2 && trackId) {
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/creations/track/${orgId}/${trackId}`;

      QRCode.toDataURL(url, {
        width: 300,
        margin: 2,
        errorCorrectionLevel: 'H',
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      })
        .then((dataUrl) => {
          setQrCodeDataUrl2(dataUrl);
        })
        .catch((err) => {
          console.error(
            '[QR Code] Failed to generate QR code for song 2:',
            err,
          );
        });
    }
  }, [song2Ready, song2SavedId, qrCodeDataUrl2, orgId]);

  useEffect(() => {
    if (currentState === 'final') {
      const trackId = currentSongVersion === 1 ? song1SavedId : song2SavedId;
      const currentQrCode =
        currentSongVersion === 1 ? qrCodeDataUrl1 : qrCodeDataUrl2;
      const currentSongReady =
        currentSongVersion === 1 ? song1Ready : song2Ready;

      if (!currentQrCode && currentSongReady && orgId && trackId) {
        const baseUrl = window.location.origin;
        const url = `${baseUrl}/creations/track/${orgId}/${trackId}`;

        QRCode.toDataURL(url, {
          width: 300,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        })
          .then((dataUrl) => {
            if (currentSongVersion === 1) {
              setQrCodeDataUrl1(dataUrl);
            } else {
              setQrCodeDataUrl2(dataUrl);
            }
          })
          .catch((err) => {
            console.error(
              `[QR Code Final State] Failed to generate QR code for song ${currentSongVersion}:`,
              err,
            );
          });
      }
    }
  }, [
    currentState,
    currentSongVersion,
    song1Ready,
    song2Ready,
    song1SavedId,
    song2SavedId,
    qrCodeDataUrl1,
    qrCodeDataUrl2,
    orgId,
  ]);

  useEffect(() => {
    if (!audioRef.current) {
      const audio = new Audio();
      // Safari-specific configurations
      audio.preload = 'auto'; // Changed to 'auto' to preload audio data, not just metadata
      audio.crossOrigin = 'anonymous';
      audioRef.current = audio;
    }
  }, []);

  useEffect(() => {
    const handleStopGenerationAudio = () => {
      const audio = audioRef.current;
      if (audio && !audio.paused) {
        audio.pause();
        audio.currentTime = 0;
        setIsPlaying(false);
        setCurrentTime(0);
        setProgress(0);
      }
    };

    window.addEventListener('stopGenerationAudio', handleStopGenerationAudio);
    return () => {
      window.removeEventListener(
        'stopGenerationAudio',
        handleStopGenerationAudio,
      );
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const isValidDuration = (value: number): boolean => {
      return isFinite(value) && value > 0 && value <= 3600;
    };

    const isValidTime = (value: number): boolean => {
      return isFinite(value) && value >= 0 && value <= 3600;
    };

    const handleTimeUpdate = () => {
      const time = audio.currentTime;
      if (isValidTime(time)) {
        setCurrentTime(time);
        currentTimeRef.current = time;
      }

      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
        const newProgress = (audio.currentTime / audio.duration) * 100;
        setProgress(newProgress);
        progressRef.current = newProgress;
      } else if (currentSongUrl && isStreamUrl(currentSongUrl)) {
        const seekable = audio.seekable;
        if (seekable.length > 0) {
          const seekableStart = seekable.start(0);
          const seekableEnd = seekable.end(seekable.length - 1);
          const seekableDuration = seekableEnd - seekableStart;

          if (seekableDuration > 0 && audio.currentTime >= seekableStart) {
            if (isValidDuration(seekableEnd)) {
              setDuration(seekableEnd);
              const relativeTime = audio.currentTime - seekableStart;
              const newProgress = Math.max(
                0,
                Math.min(100, (relativeTime / seekableDuration) * 100),
              );
              setProgress(newProgress);
              progressRef.current = newProgress;
            }
          } else if (seekableEnd > 0 && isValidDuration(seekableEnd)) {
            setDuration(seekableEnd);
            setProgress(0);
            progressRef.current = 0;
          }
        } else {
          const buffered = audio.buffered;
          if (buffered.length > 0) {
            const bufferedEnd = buffered.end(buffered.length - 1);
            if (bufferedEnd > 0 && isValidDuration(bufferedEnd)) {
              setDuration(bufferedEnd);
              const newProgress = Math.max(
                0,
                Math.min(100, (audio.currentTime / bufferedEnd) * 100),
              );
              setProgress(newProgress);
              progressRef.current = newProgress;
            }
          }
        }
      } else {
        setProgress(0);
        progressRef.current = 0;
      }
    };

    const handleLoadedMetadata = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      } else if (currentSongUrl && isStreamUrl(currentSongUrl)) {
        const seekable = audio.seekable;
        if (seekable.length > 0) {
          const seekableEnd = seekable.end(seekable.length - 1);
          if (seekableEnd > 0 && isValidDuration(seekableEnd)) {
            setDuration(seekableEnd);
          }
        } else {
          const buffered = audio.buffered;
          if (buffered.length > 0) {
            const bufferedEnd = buffered.end(buffered.length - 1);
            if (bufferedEnd > 0 && isValidDuration(bufferedEnd)) {
              setDuration(bufferedEnd);
            }
          }
        }
      }
    };

    const handleDurationChange = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      } else if (currentSongUrl && isStreamUrl(currentSongUrl)) {
        const seekable = audio.seekable;
        if (seekable.length > 0) {
          const seekableEnd = seekable.end(seekable.length - 1);
          if (seekableEnd > 0 && isValidDuration(seekableEnd)) {
            setDuration(seekableEnd);
          }
        } else {
          const buffered = audio.buffered;
          if (buffered.length > 0) {
            const bufferedEnd = buffered.end(buffered.length - 1);
            if (bufferedEnd > 0 && isValidDuration(bufferedEnd)) {
              setDuration(bufferedEnd);
            }
          }
        }
      }
    };

    const handleProgress = () => {
      if (currentSongUrl && isStreamUrl(currentSongUrl)) {
        if (!audio.duration || !isFinite(audio.duration)) {
          const seekable = audio.seekable;
          if (seekable.length > 0) {
            const seekableEnd = seekable.end(seekable.length - 1);
            if (
              seekableEnd > 0 &&
              isValidDuration(seekableEnd) &&
              seekableEnd > duration
            ) {
              setDuration(seekableEnd);
              setProgress((audio.currentTime / seekableEnd) * 100);
            }
          } else {
            const buffered = audio.buffered;
            if (buffered.length > 0) {
              const bufferedEnd = buffered.end(buffered.length - 1);
              if (
                bufferedEnd > 0 &&
                isValidDuration(bufferedEnd) &&
                bufferedEnd > duration
              ) {
                setDuration(bufferedEnd);
                setProgress((audio.currentTime / bufferedEnd) * 100);
              }
            }
          }
        }
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      setProgress(0);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleWaiting = () => {
      if (!isPlaying || !currentSongUrl) return;

      const isStream = isStreamUrl(currentSongUrl);
      if (!isStream) {
        setIsBuffering(true);
        return;
      }

      const buffered = audio.buffered;
      if (buffered.length > 0) {
        const bufferedEnd = buffered.end(buffered.length - 1);
        const currentTime = audio.currentTime;
        const bufferThreshold = 1.0;

        if (currentTime >= bufferedEnd - bufferThreshold) {
          setIsBuffering(true);
        }
      } else {
        setIsBuffering(true);
      }
    };
    const handleCanPlay = () => {
      setIsBuffering(false);
    };
    const handleSeeked = () => {
      const time = audio.currentTime;
      if (isValidTime(time)) {
        setCurrentTime(time);
        currentTimeRef.current = time;
      }

      if (audio.duration && isFinite(audio.duration)) {
        const newProgress = (audio.currentTime / audio.duration) * 100;
        setProgress(newProgress);
        progressRef.current = newProgress;
      } else if (currentSongUrl && isStreamUrl(currentSongUrl)) {
        const seekable = audio.seekable;
        console.log('seekable', seekable);
        if (seekable.length > 0) {
          const seekableStart = seekable.start(0);
          const seekableEnd = seekable.end(seekable.length - 1);
          const seekableDuration = seekableEnd - seekableStart;
          console.log('seekableDuration', seekableDuration);

          if (
            seekableDuration > 0 &&
            isValidDuration(seekableEnd) &&
            audio.currentTime >= seekableStart
          ) {
            const relativeTime = audio.currentTime - seekableStart;
            const newProgress = Math.max(
              0,
              Math.min(100, (relativeTime / seekableDuration) * 100),
            );
            setProgress(newProgress);
            progressRef.current = newProgress;
          } else if (seekableEnd > 0 && isValidDuration(seekableEnd)) {
            setProgress(0);
            progressRef.current = 0;
          }
        } else {
          const buffered = audio.buffered;
          if (buffered.length > 0) {
            const bufferedEnd = buffered.end(buffered.length - 1);
            if (bufferedEnd > 0 && isValidDuration(bufferedEnd)) {
              const newProgress = Math.max(
                0,
                Math.min(100, (audio.currentTime / bufferedEnd) * 100),
              );
              setProgress(newProgress);
              progressRef.current = newProgress;
            }
          }
        }
      } else {
        setProgress(0);
        progressRef.current = 0;
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('progress', handleProgress);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('seeked', handleSeeked);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('progress', handleProgress);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('seeked', handleSeeked);
    };
  }, [currentSongUrl, currentSongVersion]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    console.log('[Audio Effect] Running', {
      currentSongVersion,
      currentSongReady,
      currentSongUrl,
      previousSongVersion: previousSongVersionRef.current,
    });

    if (!currentSongReady || !currentSongUrl) {
      console.log('[Audio Effect] Early return - song not ready or no URL', {
        currentSongReady,
        currentSongUrl,
      });
      return;
    }

    const trackUrl = currentSongUrl;

    const isSongVersionChanging =
      previousSongVersionRef.current !== currentSongVersion;

    if (isSongVersionChanging) {
      previousSongVersionRef.current = currentSongVersion;
    }

    const isSwitchingSong = audio.src && audio.src !== trackUrl;

    // Don't early return if song version is changing (user swipe)
    if (!isSwitchingSong && !isSongVersionChanging && audio.src === trackUrl) {
      if (waveformData.length > 0) {
        return;
      }
    }

    const wasPlaying = !audio.paused && audio.src && audio.src !== trackUrl;
    const isUrlChanging = audio.src && audio.src !== trackUrl;
    // Only prevent reload if URL is changing automatically (poll finish), not on user swipe
    const shouldPreventReload =
      isUrlChanging && isPlaying && !isSongVersionChanging;

    if (shouldPreventReload) {
      return;
    }

    if (!isSongVersionChanging) {
      previousSongVersionRef.current = currentSongVersion;
    }

    let handleCanPlay: (() => void) | null = null;

    // Always update audio source if song version is changing or URL is different
    const needsAudioUpdate =
      isSongVersionChanging || audio.src !== trackUrl || isSwitchingSong;

    if (needsAudioUpdate) {
      console.log('[Audio] Needs update', {
        isSongVersionChanging,
        audioSrc: audio.src,
        trackUrl,
        audioSrcMatches: audio.src === trackUrl,
        isSwitchingSong,
      });
    }

    if (needsAudioUpdate) {
      if (isSongVersionChanging || isSwitchingSong) {
        console.log('[Audio] Resetting audio state', {
          isSongVersionChanging,
          isSwitchingSong,
        });
        try {
          audio.pause();
          audio.currentTime = 0;
          setCurrentTime(0);
          setProgress(0);
          setIsPlaying(false);
        } catch (error) {
          console.error('Error pausing audio:', error);
        }
      }

      try {
        console.log('[Audio] Setting audio.src', {
          from: audio.src,
          to: trackUrl,
          isSongVersionChanging,
        });

        if (wasPlaying && currentSongReady) {
          handleCanPlay = () => {
            if (audio) {
              audio
                .play()
                .then(() => {
                  setIsPlaying(true);
                })
                .catch((error) => {
                  console.error('Error auto-playing after song switch:', error);
                });
            }
            if (handleCanPlay && audio) {
              audio.removeEventListener('canplay', handleCanPlay);
            }
          };

          if (audio) {
            audio.addEventListener('canplay', handleCanPlay);
          }
        }

        // Safari fix: Reset and configure audio element before setting new source
        audio.pause();
        audio.removeAttribute('src');
        audio.load();

        // Set Safari-friendly attributes
        audio.crossOrigin = 'anonymous';
        audio.preload = 'auto'; // Use 'auto' to preload audio data

        audio.src = trackUrl;
        audio.load();

        console.log('[Audio] audio.src set, new value:', audio.src);
      } catch (error) {
        console.error('Error switching audio source:', error);
      }

      return () => {
        if (handleCanPlay && audio) {
          audio.removeEventListener('canplay', handleCanPlay);
        }
      };
    }
  }, [currentSongReady, currentSongUrl, currentSongVersion]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const handleNext = useCallback(() => {
    if (currentState === 'lyrics') {
      if (!hasLyrics) return;
      if (hasLyrics && !song1Ready) {
        onProduce();
      }
      setCurrentState('cover');
    } else if (currentState === 'cover') {
      if (!currentSavedId) return;
      setCurrentState('final');
    }
  }, [currentState, hasLyrics, song1Ready, onProduce, currentSavedId]);

  const handleShare = useCallback(() => {
    if (currentState === 'cover' && currentSavedId) {
      setCurrentState('final');
    }
  }, [currentState, currentSavedId]);

  const handleSwitchToNextVariant = useCallback(() => {
    if (!hasMultipleVariations) return;
    const nextVersion: 1 | 2 = currentSongVersion === 1 ? 2 : 1;
    const nextThumbnail =
      nextVersion === 1
        ? musicGeneration?.thumbnailUrl1
        : musicGeneration?.thumbnailUrl2;
    if (!nextThumbnail) return;
    setCurrentSongVersion(nextVersion);
  }, [
    hasMultipleVariations,
    currentSongVersion,
    musicGeneration?.thumbnailUrl1,
    musicGeneration?.thumbnailUrl2,
  ]);

  useEffect(() => {
    setHandleNextFunction(handleNext);
    setHandleShareFunction(handleShare);
    setSwitchToNextVariantFunction(handleSwitchToNextVariant);

    return () => {
      setHandleNextFunction(null);
      setHandleShareFunction(null);
      setSwitchToNextVariantFunction(null);
    };
  }, [handleNext, handleShare, handleSwitchToNextVariant]);

  useEffect(() => {
    if (
      currentState === 'final' &&
      currentSongReady &&
      currentSavedId &&
      qrCodeSavedIdRef.current !== currentSavedId
    ) {
      if (!orgId) return;
      const trackId = currentSavedId;
      if (trackId) {
        const baseUrl = window.location.origin;
        const url = `${baseUrl}/creations/track/${orgId}/${trackId}`;

        QRCode.toDataURL(url, {
          width: 300,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        })
          .then((dataUrl) => {
            if (currentSongVersion === 1) {
              setQrCodeDataUrl1(dataUrl);
            } else {
              setQrCodeDataUrl2(dataUrl);
            }
            qrCodeSavedIdRef.current = currentSavedId;
          })
          .catch((err) => {
            console.error('[QR Code] Failed to generate QR code:', err);
          });
      }
    }
  }, [
    currentState,
    currentSongReady,
    orgId,
    currentSavedId,
    currentSongVersion,
  ]);

  useEffect(() => {
    setContestEmail(null);
    contestEnteredIdsRef.current.clear();
  }, [musicGeneration?.taskId]);

  const submitContestEntry = useCallback(
    (email: string, creationIds: string[]) =>
      Promise.all(
        creationIds.map((id) => {
          contestEnteredIdsRef.current.add(id);
          return enterContest({
            id,
            data: { email, ...(contestId ? { contestId } : {}) },
          }).catch((error: unknown) => {
            contestEnteredIdsRef.current.delete(id);
            throw error;
          });
        }),
      ),
    [contestId, enterContest],
  );

  // Both variants are "the song" to the visitor, so every saved one is entered
  const handleContestSubmit = useCallback(
    async (email: string) => {
      const savedIds = [song1SavedId, song2SavedId].filter(
        (id): id is string => !!id,
      );
      await submitContestEntry(email, savedIds);
      setContestEmail(email);
    },
    [song1SavedId, song2SavedId, submitContestEntry],
  );

  // A variant that finishes saving after the entry went in gets entered too
  useEffect(() => {
    if (!contestEmail) return;
    const pendingIds = [song1SavedId, song2SavedId].filter(
      (id): id is string => !!id && !contestEnteredIdsRef.current.has(id),
    );
    if (pendingIds.length === 0) return;
    submitContestEntry(contestEmail, pendingIds).catch((error: unknown) => {
      console.error('[Contest] Failed to enter late-saved variant:', error);
    });
  }, [contestEmail, song1SavedId, song2SavedId, submitContestEntry]);

  const handleStartOver = useCallback(() => {
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
      setProgress(0);
    }

    sendStartOverMessage();
    navigate('/');
  }, [navigate]);

  const handlePrint = useCallback(async () => {
    if (!displayCover || isPrinting) return;

    setIsPrinting(true);
    try {
      if (currentQrCodeDataUrl) {
        const compositedImage = await compositeImageWithQR(
          displayCover,
          currentQrCodeDataUrl,
        );
        sendPrintMessage(compositedImage);
      } else {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            setIsPrinting(false);
            return;
          }
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          const base64 = canvas.toDataURL('image/png');
          sendPrintMessage(base64);
          setIsPrinting(false);
        };
        img.onerror = () => {
          console.error('Failed to load image for printing');
          setIsPrinting(false);
        };
        img.src = displayCover;
        return;
      }
      setIsPrinting(false);
    } catch (error) {
      console.error('Error preparing image for print:', error);
      setIsPrinting(false);
    }
  }, [displayCover, currentQrCodeDataUrl, isPrinting]);

  const handlePlayPause = useCallback(async () => {
    if (!audioRef.current || !currentSongReady) return;
    try {
      if (isPlaying) {
        audioRef.current.pause();
        playRetryAttemptRef.current = false;
      } else {
        sendStopTalkingMessage();

        const audio = audioRef.current;

        if (!audio.src || audio.src === '') {
          console.error('[Audio] No source set');
          return;
        }

        console.log('[Audio] Current state:', {
          readyState: audio.readyState,
          src: audio.src,
          networkState: audio.networkState,
          error: audio.error,
        });

        const hasError = audio.error !== null;
        const isRetrying = playRetryAttemptRef.current;

        if (hasError || (isRetrying && audio.readyState < 2)) {
          console.log('[Audio] Resetting audio element for Safari');

          const currentSrc = audio.src;

          audio.pause();
          audio.removeAttribute('src');
          audio.load();

          audio.crossOrigin = 'anonymous';
          audio.preload = 'auto';

          audio.src = currentSrc;
          audio.load();

          console.log(
            '[Audio] Audio reset complete, waiting for ready state...',
          );
        }

        if (audio.readyState < 2) {
          console.log('[Audio] Waiting for audio to be ready...');
          setIsBuffering(true);
          try {
            await new Promise<void>((resolve, reject) => {
              const timeoutId = setTimeout(() => {
                console.log('[Audio] Load timeout');
                reject(new Error('Audio load timeout'));
              }, 10000);

              const onCanPlay = () => {
                console.log('[Audio] canplay event fired');
                clearTimeout(timeoutId);
                cleanup();
                resolve();
              };

              const onLoadedData = () => {
                console.log('[Audio] loadeddata event fired');
                clearTimeout(timeoutId);
                cleanup();
                resolve();
              };

              const onError = (e: Event) => {
                console.error('[Audio] Error event fired during load:', e);
                clearTimeout(timeoutId);
                cleanup();
                reject(new Error('Audio load failed'));
              };

              const cleanup = () => {
                audio.removeEventListener('canplay', onCanPlay);
                audio.removeEventListener('loadeddata', onLoadedData);
                audio.removeEventListener('error', onError);
              };

              audio.addEventListener('canplay', onCanPlay, { once: true });
              audio.addEventListener('loadeddata', onLoadedData, {
                once: true,
              });
              audio.addEventListener('error', onError, { once: true });

              if (audio.readyState >= 2) {
                console.log(
                  '[Audio] ReadyState already >= 2, resolving immediately',
                );
                clearTimeout(timeoutId);
                cleanup();
                resolve();
              }
            });
          } catch (waitError) {
            console.error('[Audio] Failed to load audio:', waitError);
            setIsBuffering(false);

            if (!playRetryAttemptRef.current) {
              console.log('[Audio] Auto-retrying play after failure...');
              playRetryAttemptRef.current = true;
              setTimeout(() => {
                handlePlayPause();
              }, 100);
              return;
            } else {
              playRetryAttemptRef.current = false;
              console.error('[Audio] Retry failed, user needs to try again');
              return;
            }
          }
        }

        if (audio.readyState < 2) {
          console.error('[Audio] ReadyState still < 2 after wait, cannot play');
          setIsBuffering(false);

          if (!playRetryAttemptRef.current) {
            console.log(
              '[Audio] Auto-retrying play after readyState check failure...',
            );
            playRetryAttemptRef.current = true;
            setTimeout(() => {
              handlePlayPause();
            }, 100);
            return;
          } else {
            playRetryAttemptRef.current = false;
            return;
          }
        }

        console.log('[Audio] Attempting to play...');
        setIsBuffering(false);
        await audio.play();
        console.log('[Audio] Play successful');
        playRetryAttemptRef.current = false;
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error playing/pausing audio:', error);
      }
    }
  }, [isPlaying, currentSongReady]);

  const isInteractiveElement = (target: EventTarget | null): boolean => {
    if (!target || !(target instanceof HTMLElement)) return false;
    const element = target as HTMLElement;
    const tagName = element.tagName.toLowerCase();
    const isButton = tagName === 'button' || element.closest('button');
    const isInput =
      tagName === 'input' ||
      tagName === 'textarea' ||
      element.closest('input, textarea');
    const isLink = tagName === 'a' || element.closest('a');
    const isSelect = tagName === 'select' || element.closest('select');
    const hasPointerEvents =
      window.getComputedStyle(element).pointerEvents === 'none';
    return !!(isButton || isInput || isLink || isSelect) && !hasPointerEvents;
  };

  const handleSwipeStart = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      if (isInteractiveElement(e.target)) {
        return;
      }
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      swipeStartRef.current = { x: clientX, y: clientY };
    },
    [],
  );

  const handleSwipeMove = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      if (!swipeStartRef.current || isInteractiveElement(e.target)) {
        if (isInteractiveElement(e.target)) {
          swipeStartRef.current = null;
        }
        return;
      }
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      const deltaX = Math.abs(clientX - swipeStartRef.current.x);
      const deltaY = Math.abs(clientY - swipeStartRef.current.y);
      // Only prevent default if horizontal movement is greater
      if (deltaX > deltaY && deltaX > 10) {
        if (!('touches' in e)) {
          e.preventDefault();
        }
      }
    },
    [],
  );

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!swipeStartRef.current || isInteractiveElement(e.target)) {
      if (isInteractiveElement(e.target)) {
        swipeStartRef.current = null;
      }
      return;
    }
    const clientX = e.touches[0].clientX;
    const clientY = e.touches[0].clientY;
    const deltaX = Math.abs(clientX - swipeStartRef.current.x);
    const deltaY = Math.abs(clientY - swipeStartRef.current.y);
    if (deltaX > deltaY && deltaX > 10) {
      e.preventDefault();
    }
  }, []);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (isInteractiveElement(e.target)) {
      return;
    }
    const clientX = e.touches[0].clientX;
    const clientY = e.touches[0].clientY;
    swipeStartRef.current = { x: clientX, y: clientY };
  }, []);

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (
        !swipeStartRef.current ||
        isInteractiveElement(e.target) ||
        !hasMultipleVariations
      ) {
        if (isInteractiveElement(e.target)) {
          swipeStartRef.current = null;
        }
        return;
      }

      const clientX = e.changedTouches[0].clientX;
      const clientY = e.changedTouches[0].clientY;

      const deltaX = clientX - swipeStartRef.current.x;
      const deltaY = clientY - swipeStartRef.current.y;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      if (absDeltaX > absDeltaY && absDeltaX > 50) {
        if (deltaX > 0 && currentSongVersion === 2) {
          console.log('[Swipe TouchEnd] Changing to song version 1');
          setCurrentSongVersion(1);
        } else if (deltaX < 0 && currentSongVersion === 1) {
          console.log('[Swipe TouchEnd] Changing to song version 2');
          setCurrentSongVersion(2);
        }
      }

      swipeStartRef.current = null;
    },
    [hasMultipleVariations, currentSongVersion],
  );

  useEffect(() => {
    const container = swipeContainerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    container.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const handleSwipeEnd = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      if (
        !swipeStartRef.current ||
        !hasMultipleVariations ||
        isInteractiveElement(e.target)
      ) {
        swipeStartRef.current = null;
        return;
      }

      const clientX =
        'changedTouches' in e ? e.changedTouches[0].clientX : e.clientX;
      const clientY =
        'changedTouches' in e ? e.changedTouches[0].clientY : e.clientY;

      const deltaX = clientX - swipeStartRef.current.x;
      const deltaY = clientY - swipeStartRef.current.y;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      if (absDeltaX > absDeltaY && absDeltaX > 50) {
        if (deltaX > 0 && currentSongVersion === 2) {
          console.log('[Swipe TouchEnd] Changing to song version 1');
          setCurrentSongVersion(1);
        } else if (deltaX < 0 && currentSongVersion === 1) {
          console.log('[Swipe TouchEnd] Changing to song version 2');
          setCurrentSongVersion(2);
        }
      }

      swipeStartRef.current = null;
    },
    [hasMultipleVariations, currentSongVersion],
  );

  const handleSeek = useCallback(
    (percentage: number) => {
      if (!audioRef.current) return;

      const audio = audioRef.current;
      const isStream = currentSongUrl ? isStreamUrl(currentSongUrl) : false;

      const isValidDuration = (value: number): boolean => {
        return isFinite(value) && value > 0 && value <= 3600;
      };

      const isValidTime = (value: number): boolean => {
        return isFinite(value) && value >= 0 && value <= 3600;
      };

      if (isStream && (!audio.duration || !isFinite(audio.duration))) {
        const seekable = audio.seekable;
        console.log('seekable', seekable);
        if (seekable.length > 0) {
          const seekableStart = seekable.start(0);
          const seekableEnd = seekable.end(seekable.length - 1);
          const seekableDuration = seekableEnd - seekableStart;

          if (seekableDuration > 0 && isValidDuration(seekableEnd)) {
            const newTime =
              seekableStart + (percentage / 100) * seekableDuration;
            const clampedTime = Math.max(
              seekableStart,
              Math.min(seekableEnd, newTime),
            );

            try {
              audio.currentTime = clampedTime;
              if (isValidTime(clampedTime)) {
                setCurrentTime(clampedTime);
              }
              const progressPercent =
                ((clampedTime - seekableStart) / seekableDuration) * 100;
              setProgress(progressPercent);
            } catch (error) {
              if (import.meta.env.DEV) {
                console.error('Error seeking audio:', error);
              }
            }
          }
        } else {
          const buffered = audio.buffered;
          if (buffered.length > 0) {
            const bufferedEnd = buffered.end(buffered.length - 1);
            if (bufferedEnd > 0 && isValidDuration(bufferedEnd)) {
              const newTime = (percentage / 100) * bufferedEnd;
              try {
                audio.currentTime = newTime;
                if (isValidTime(newTime)) {
                  setCurrentTime(newTime);
                }
                setProgress(percentage);
              } catch (error) {
                if (import.meta.env.DEV) {
                  console.error('Error seeking audio:', error);
                }
              }
            }
          }
        }
      } else {
        const seekDuration =
          duration ||
          (audio.duration && isFinite(audio.duration) ? audio.duration : 0);
        if (!seekDuration || seekDuration === 0) return;

        const newTime = (percentage / 100) * seekDuration;
        try {
          audio.currentTime = newTime;
          if (isValidTime(newTime)) {
            setCurrentTime(newTime);
          }
          setProgress(percentage);
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('Error seeking audio:', error);
          }
        }
      }
    },
    [duration, currentSongUrl],
  );

  const trackDuration =
    currentSongVersion === 1
      ? musicGeneration?.duration1 || 120
      : musicGeneration?.duration2 || 120;
  const displayDuration = convertSecondsToTime(
    Math.round(Number(trackDuration) || 120),
  );

  const defaultWaveformData = useMemo(
    () =>
      Array.from({ length: 60 }, (_, i) => {
        const baseHeight = 15;
        const variation = Math.sin((i / 60) * Math.PI * 4) * 20;
        const random = (i * 17) % 15;
        return Math.max(8, baseHeight + variation + random);
      }),
    [],
  );

  // Use real waveform data if available, otherwise use default
  const displayWaveformData =
    waveformData.length > 0 ? waveformData : defaultWaveformData;

  return (
    <div
      ref={swipeContainerRef}
      className="fixed inset-0 flex flex-col text-white overflow-hidden"
      style={{
        background: USE_IMAGE_BACKGROUND
          ? `url('/assets/images/ready.png') center center / cover no-repeat`
          : isGeneratingOrRegenerating
          ? 'linear-gradient(180deg, #7136BE 0%, #7136BE 30%, #D400A1 60%, #D400A1 80%, #000 100%)'
          : 'linear-gradient(180deg, #7136BE 0%, #7136BE 30%, #7136BE 60%, #7136BE 80%, #000 100%)',
      }}
      onMouseDown={handleSwipeStart}
      onMouseMove={handleSwipeMove}
      onMouseUp={handleSwipeEnd}
      onMouseLeave={handleSwipeEnd}>
      {/* Ambient background layer - only show when not using image background */}
      {!USE_IMAGE_BACKGROUND && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: isGeneratingOrRegenerating
              ? `radial-gradient(900px 900px at 12% 50%, rgba(212,0,161,0.35), transparent 60%),
                 radial-gradient(900px 900px at 88% 50%, rgba(212,0,161,0.35), transparent 60%),
                 radial-gradient(900px 650px at 50% 10%, rgba(212,0,161,0.25), transparent 60%),
                 radial-gradient(900px 700px at 50% 95%, rgba(25,54,104,0.35), transparent 65%)`
              : `radial-gradient(900px 900px at 12% 50%, rgba(113,54,190,0.35), transparent 60%),
                 radial-gradient(900px 900px at 88% 50%, rgba(113,54,190,0.35), transparent 60%),
                 radial-gradient(900px 650px at 50% 10%, rgba(113,54,190,0.25), transparent 60%),
                 radial-gradient(900px 700px at 50% 95%, rgba(25,54,104,0.35), transparent 65%)`,
            filter: 'blur(80px)',
            animation: 'bgFloat 14s ease-in-out infinite',
          }}
        />
      )}
      {/* Top gradient overlay - only show when not using image background */}
      {!USE_IMAGE_BACKGROUND && (
        <div
          className="absolute inset-0 pointer-events-none z-10"
          style={{
            background:
              'linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0.00) 100%)',
            backdropFilter: 'blur(0px)',
          }}
        />
      )}
      {/* Dark center vignette - only show when not using image background */}
      {!USE_IMAGE_BACKGROUND && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.1) 70%, transparent 100%)',
          }}
        />
      )}
      {/* Full-screen glow animation when using image background and generating/regenerating */}
      <StationGlowAnimation
        isVisible={USE_IMAGE_BACKGROUND && isGeneratingOrRegenerating}
      />
      <StationContestModal
        isOpen={isContestModalOpen}
        onClose={() => setIsContestModalOpen(false)}
        onSubmit={handleContestSubmit}
      />
      <div className="relative z-20 flex flex-col h-full w-full p-10 sm:p-12 md:p-14">
        {/* Next/Share button - positioned outside the card */}
        {((currentState === 'lyrics' && hasLyrics) ||
          (currentState === 'cover' && currentSavedId)) && (
          <div className="absolute top-11 right-8 sm:right-12 md:right-16 z-20 flex justify-end items-center gap-3">
            {isContestMode && currentSavedId && (
              <button
                onClick={() => setIsContestModalOpen(true)}
                disabled={!!contestEmail}
                className="px-6 py-4 bg-fuchsia-700 rounded-full text-white text-xl font-medium font-['DM_Sans'] leading-6 hover:bg-fuchsia-800 transition-colors shadow-[inset_0px_-3px_4.5px_0px_rgba(0,0,0,0.15)] shadow-[inset_0px_3px_4.5px_0px_rgba(255,150,217,0.50)] disabled:opacity-70 disabled:cursor-default disabled:hover:bg-fuchsia-700">
                {contestEmail
                  ? t('station.contest.entered')
                  : t('station.contest.enter')}
              </button>
            )}
            {currentState === 'cover' && (
              <button
                onClick={handleStartOver}
                className="px-6 py-4 bg-fuchsia-700 rounded-full text-white text-xl font-medium font-['DM_Sans'] leading-6 hover:bg-fuchsia-800 transition-colors shadow-[inset_0px_-3px_4.5px_0px_rgba(0,0,0,0.15)] shadow-[inset_0px_3px_4.5px_0px_rgba(255,150,217,0.50)]">
                {t('generation.startOver')}
              </button>
            )}
            <button
              onClick={handleNext}
              className="px-6 py-4 bg-fuchsia-700 rounded-full text-white text-xl font-medium font-['DM_Sans'] leading-6 hover:bg-fuchsia-800 transition-colors shadow-[inset_0px_-3px_4.5px_0px_rgba(0,0,0,0.15)] shadow-[inset_0px_3px_4.5px_0px_rgba(255,150,217,0.50)]">
              {currentState === 'cover' && currentSavedId
                ? t('generation.share')
                : t('generation.next')}
            </button>
          </div>
        )}
        {currentState === 'final' && (
          <div className="absolute top-11 right-8 sm:right-12 md:right-16 z-20 flex justify-end items-center gap-3">
            {isContestMode && currentSavedId && (
              <button
                onClick={() => setIsContestModalOpen(true)}
                disabled={!!contestEmail}
                className="px-6 py-4 bg-fuchsia-700 rounded-full text-white text-xl font-medium font-['DM_Sans'] leading-6 hover:bg-fuchsia-800 transition-colors shadow-[inset_0px_-3px_4.5px_0px_rgba(0,0,0,0.15)] shadow-[inset_0px_3px_4.5px_0px_rgba(255,150,217,0.50)] disabled:opacity-70 disabled:cursor-default disabled:hover:bg-fuchsia-700">
                {contestEmail
                  ? t('station.contest.entered')
                  : t('station.contest.enter')}
              </button>
            )}
            <button
              onClick={handleStartOver}
              className="px-6 py-4 bg-fuchsia-700 rounded-full text-white text-xl font-medium font-['DM_Sans'] leading-6 hover:bg-fuchsia-800 transition-colors shadow-[inset_0px_-3px_4.5px_0px_rgba(0,0,0,0.15)] shadow-[inset_0px_3px_4.5px_0px_rgba(255,150,217,0.50)]">
              Start Over
            </button>
            {isPrintEnabled && displayCover && (
              <button
                onClick={handlePrint}
                disabled={isPrinting}
                className="px-6 py-4 bg-fuchsia-700 rounded-full text-white text-xl font-medium font-['DM_Sans'] leading-6 hover:bg-fuchsia-800 transition-colors shadow-[inset_0px_-3px_4.5px_0px_rgba(0,0,0,0.15)] shadow-[inset_0px_3px_4.5px_0px_rgba(255,150,217,0.50)] disabled:opacity-50 disabled:cursor-not-allowed">
                {isPrinting ? t('generation.printing') : t('generation.print')}
              </button>
            )}
          </div>
        )}
        <div
          className="relative flex flex-col w-full"
          style={{
            marginTop: '120px',
            height: 'calc(100% - 120px)',
          }}>
          {isGeneratingOrRegenerating && !USE_IMAGE_BACKGROUND && (
            <>
              <div
                className="absolute inset-0 w-full h-full pointer-events-none rounded-[36px]"
                style={{
                  border: '20px solid rgba(212, 0, 161, 0.8)',
                  boxShadow:
                    '0 0 80px rgba(212, 0, 161, 0.6), inset 0 0 80px rgba(212, 0, 161, 0.3)',
                  animation: 'borderGlow 2.4s ease-in-out infinite',
                }}
              />
              <div
                className="absolute pointer-events-none rounded-[36px]"
                style={{
                  left: '2.6%',
                  top: '0.9%',
                  width: '94.8%',
                  height: '98.2%',
                  border: '30px solid rgba(212, 0, 161, 0.3)',
                  boxShadow: '0 0 120px rgba(212, 0, 161, 0.5)',
                  filter: 'blur(40px)',
                  animation: 'borderGlow 2.4s ease-in-out infinite',
                }}
              />
            </>
          )}
          {currentState === 'lyrics' && (
            <StationLyricsView
              title={displayTitle}
              lyrics={displayLyrics}
              lyricsContentRef={lyricsContentRef}
              isUserEditing={isUserEditing}
              onFocus={() => setIsUserEditing(true)}
              onBlur={() => {
                setIsUserEditing(false);
                if (lyricsContentRef.current) {
                  onLyricsChange(lyricsContentRef.current.innerText || '');
                }
              }}
              onInput={() => {
                if (lyricsContentRef.current) {
                  onLyricsChange(lyricsContentRef.current.innerText || '');
                }
              }}
              isLoading={!hasLyrics && !isRegeneratingLyrics}
            />
          )}

          {(currentState === 'cover' || currentState === 'final') && (
            <div
              ref={swipeContainerRef}
              className="flex-1 w-full"
              style={{ overflowX: 'hidden', overflowY: 'auto' }}>
              {currentState === 'cover' && (
                <StationCoverView
                  title={displayTitle}
                  coverUrl={displayCoverWithQR}
                  isSongReady={currentSongReady}
                  isRegeneratingImage={isRegeneratingImage}
                  showGlassEffect={showCoverGlassEffect}
                  isGeneratingImage={isGeneratingImage}
                  isPlaying={isPlaying}
                  isBuffering={isBuffering}
                  duration={duration}
                  currentTime={currentTime}
                  displayDuration={displayDuration}
                  waveformData={displayWaveformData}
                  progress={progress}
                  volume={volume}
                  onVolumeChange={setVolume}
                  onPlayPause={handlePlayPause}
                  onSeek={handleSeek}
                  onCardClick={() => onCardClick?.(currentSongVersion)}
                  hasMultipleVariations={hasMultipleVariations}
                  countdownSeconds={countdownSeconds}
                  showExtendedMessage={showExtendedMessage}
                />
              )}

              {currentState === 'final' && (
                <StationFinalView
                  title={displayTitle}
                  coverUrl={displayCover}
                  qrCodeDataUrl={currentQrCodeDataUrl}
                  isSongReady={currentSongReady}
                  isPlaying={isPlaying}
                  isBuffering={isBuffering}
                  duration={duration}
                  currentTime={currentTime}
                  displayDuration={displayDuration}
                  waveformData={displayWaveformData}
                  progress={progress}
                  volume={volume}
                  onVolumeChange={setVolume}
                  onPlayPause={handlePlayPause}
                  onSeek={handleSeek}
                  hasMultipleVariations={hasMultipleVariations}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
